import { useEffect, useRef, useState } from 'react';
import '../Dashboard.scss';
import * as echarts from 'echarts';
import IconButton from '@mui/material/IconButton';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { downloadCSV, downloadIcon } from './utils';

const Chart = ({
  type,
  emissions,
  ci,
}: {
  type: string;
  emissions: any;
  ci: boolean;
}) => {
  const chartRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const chart = echarts.init(chartRef.current);
    const emissionRates = emissions.map(
      (e: { [x: string]: any }) => e['emissions'],
    );

    const desiredNumberOfBins = 50;
    const uniqueRates = Array.from(new Set(emissionRates));
    const numberOfBins = Math.min(desiredNumberOfBins, uniqueRates.length);

    const minRate = Math.min(...emissionRates);
    const maxRate = Math.max(...emissionRates);

    const binSize = numberOfBins > 1 ? (maxRate - minRate) / numberOfBins : 1;

    const bins: { count: number; value: number }[] = [];

    for (let i = 0; i < numberOfBins; i++) {
      const start = minRate + i * binSize;
      const end = start + binSize;
      const midValue = (start + end) / 2;
      bins.push({ value: midValue, count: 0 });
    }

    emissionRates.forEach((rate: number) => {
      const binIndex = Math.min(
        Math.floor((rate - minRate) / binSize),
        numberOfBins - 1,
      );
      if (binIndex >= 0 && binIndex < bins.length) {
        bins[binIndex].count += 1;
      }
    });

    const meanValue =
      emissionRates.reduce((sum: any, value: any) => sum + value, 0) /
      emissionRates.length;

    const option = {
      title: {
        text: type,
        left: 'center',
      },
      xAxis: {
        type: 'category',
        data: bins.map((bin) => bin.value.toFixed(1)),
        name: 'Emission Rate (kg/h)',
        nameLocation: 'middle',
        nameGap: 25,
      },
      yAxis: {
        type: 'value',
        name: 'Frequency',
      },
      series: [
        {
          data: bins.map((bin) => bin.count),
          type: 'bar',
          name: 'Frequency',
          markLine: {
            symbol: ['none', 'none'],
            data: [
              {
                xAxis: bins.findIndex(
                  (bin) =>
                    meanValue >= bin.value && meanValue < bin.value + binSize,
                ),
                label: {
                  formatter: `Mean: ${meanValue.toFixed(1)}`,
                  position: 'insideEndTop',
                },
                lineStyle: {
                  color: 'red',
                  width: 2,
                  type: 'dashed',
                },
              },
            ],
          },
        },
      ],
      tooltip: {
        trigger: 'axis',
      },
      toolbox: {
        right: '1%',
        feature: {
          saveAsImage: {},
          myDownload: {
            show: true,
            title: 'Download data',
            icon: downloadIcon,
            onclick: function () {
              downloadCSV(bins);
            },
          },
          dataZoom: [
            {
              type: 'slider',
            },
            {
              type: 'inside',
            },
          ],
        },
      },
      animation: false,
    };

    chart.setOption(option);

    return () => {
      chart.dispose();
    };
  }, [type, emissions, isFullscreen]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className={`chart-container ${isFullscreen ? 'fullscreen' : ''}`}>
      <IconButton
        onClick={toggleFullscreen}
        style={{
          position: 'absolute',
          top: '0px',
          left: '10px',
          zIndex: 1000,
        }}
      >
        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </IconButton>
      <div ref={chartRef} className={'chart'} />
    </div>
  );
};

export default Chart;
